import { FunctionComponent, useContext, useState } from "react";
import { DataImportResult } from "../../../../interfaces/common/data-importing";
import { CustomerType } from "../../../../interfaces/common/customer";
import GenericTable from "../../../common/generic-table/generic-table";
import { displayMultipleLocations } from "../../../../utils/locations";
import CustomDialog from "../../../common/custom-dialog/custom-dialog";
import { DialogContent, DialogTitle, Typography, Chip, Button, CircularProgress, Alert } from "@mui/material";
import { isMobile, isTablet } from "react-device-detect";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AuthContext } from "../../../../contexts/user-context/user-context";
import axios, { AxiosResponse, isAxiosError } from "axios";
import { API_ENDPOINT } from "../../../../configurations/global.config";
import { useSnackBar } from "../../../../contexts/snackbar";
import { ErrorsFlattenner } from "../../../common/errors-flattenner/errors-flattenner";

interface PreviewCustomersProps {
    data: DataImportResult<CustomerType>;
    onClose: () => void;
    onSave: () => void;
}

const PreviewCustomers: FunctionComponent<PreviewCustomersProps> = ({ data, onClose, onSave }) => {
    const [isSaving, setIsSaving] = useState(false)
    const {config} = useContext(AuthContext)
    const [response, setResponse] = useState<AxiosResponse>()
    const { showMessage } = useSnackBar()
    
    const handleSave = async () => {
        setIsSaving(true)
        setResponse(undefined)
        try {
            const endpoint = `${API_ENDPOINT}/v2/customers/import/save/?resolve_addresses=true`
            await axios.post(endpoint, data.draft.filter(item => item._op == "create"), config)
            showMessage("Records saved successfully.")
            onSave()
        } catch(e) {
            if (isAxiosError(e)) {
                setResponse(response)
            }

            showMessage("Failed to save records.", 'error')
        } finally {
            setIsSaving(false)
        }

    }

    const nothingToSave = data.draft.filter(item => item._op == 'create').length == 0
    return (
        <CustomDialog open={true} onClose={onClose} maxWidth={"lg"} fullWidth fullScreen={(isMobile && !isTablet)}>
            <DialogTitle>
                <Typography align="center" variant="h4" component={"h4"}>
                    Import Preview
                </Typography>

                 <Typography align="center" variant="body1" component={"h4"}>
                    Once you save the records, AI will automatically detect the missing area 😁
                </Typography>
                  
            </DialogTitle>
            <DialogContent>
                <div style={{display: "flex", justifyContent: "center", gap:4}}>
                    <Button variant="outlined" disabled={isSaving || nothingToSave} onClick={handleSave}>Save {isSaving && <CircularProgress size={20} sx={{ml:2}}/>}</Button>
                    <Button  disabled={isSaving} onClick={onClose} variant="outlined" color="error">Cancel</Button>
                </div>

                <Alert sx={{mt: 2, mb: 2}} severity="info">
                    Records marked with <b>Already Exists</b> will be skipped when saving the records. 
                </Alert>
                
                {
                    nothingToSave && <Alert sx={{mt: 2, mb: 2}} severity="warning">
                    No new records were found. Nothing to save.
                </Alert>
                }

                {
                    response && <><Alert
                                severity={
                                  response && response.status >= 200 && response.status < 300
                                    ? 'success'
                                    : 'error'
                                }
                              >
                                    <ErrorsFlattenner rawErrors={response.data} />
                              </Alert>
                              <br />
                            </>
                }
                <GenericTable<CustomerType & { _op: "create" | "skip" }>
                    sourceView="customers-bulk-import-preview"
                    items={data.draft}
                    selectedItems={new Map()}
                    isLoading={false}
                    fullWidth
                    columns={[
                        {
                            id: "name",
                            header: "Name",
                            display_body: (item) => item.name
                        },
                        {
                            id: "phone",
                            header: "Phone Number",
                            display_body: (item) => item.phone_number
                        },
                        {
                            id: "email",
                            header: "Email",
                            display_body: (item) => item.email
                        },
                        {
                            id: "secondary phone",
                            header: "Secondary Phone",
                            display_body: (item) => item.secondary_phone_number ?? ""
                        },
                        {
                            id: "locations",
                            header: "Locations",
                            display_body: (item) => displayMultipleLocations(item.locations)
                        },
                        {
                            id: "status",
                            header: "Remarks",
                            display_body: (item) => item._op === "create" ? (
                                <Chip
                                    icon={<AddCircleOutlineIcon />}
                                    label="Will be Created"
                                    color="success"
                                    variant="filled"
                                    size="small"
                                />
                            ) : (
                                <Chip
                                    icon={<ErrorOutlineIcon />}
                                    label="Already Exists"
                                    color="warning"
                                    variant="filled"
                                    size="small"
                                />
                            )
                        }
                    ]}
                    getItemUID={(item) => `${item._op}${item.phone_number}${item.name}`}
                />
            </DialogContent>
        </CustomDialog>
    );
};

export default PreviewCustomers;
